import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchModuleData, selectModuleData } from '../redux/moduleDataSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faClose, faCalendar } from '@fortawesome/free-solid-svg-icons'
import { concatFiltersOnUrl } from '../utils/listViewHelper'
import { selectAuth } from "../redux/authSlice";
import { selectUser } from "../redux/userSlice";
import { selectDropdownOptions, selectDropdowns } from '../redux/dropdownSlice'
import { MODULE_FIELD_TYPES } from '../constants'
import Select from 'react-select'
import { selectPortfolios } from '../redux/portfolioSlice'
import { reactSelectClassNames } from '../helpers/react-select-styles'
import { useForm, Controller } from 'react-hook-form'
import DatePicker from "react-datepicker";
import SpecialInstruction from './elements/SpecialInstruction'
import AsyncSelect from 'react-select/async'
import { useRequest } from '../hooks/UseRequest'

export default function SearchAndFilterable({
  searchableFields,
  url,
  setPartialLoad,
  searchKeyWords,
  setSearchKeyWords,
  setSearchFilter,
  filterLocally,
  headers,
  module
}) {
  const [originalDataBeforeFiltering, setOriginalDataBeforeFiltering] = useState(null)
  const [filters, setFilters] = useState([])
  const [searchTimeout, setSearchTimeout] = useState(0)
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [advancedSearchFilters, setAdvancedSearchFilters] = useState([])
  const [queryText, setQueryText] = useState(null);

  const searchRef = useRef(null);

  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const active = useSelector(selectUser)
  const moduleData = useSelector(selectModuleData)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const dropDowns = useSelector(selectDropdowns)
  const portfolios = useSelector(selectPortfolios);
  const [validPortfolios, setValidPortfolios] = useState([]);
  let request = useRequest();

  // add loading spinner
  const [isSearching, setIsSearching] = useState(false)

  // Used for Non System Dropdowns
  const parseLabel = ((field) => {
    let option = field.name;
    if (field.label) {
      option = field.label;
    }
    return option
  })

  const buildPlaceholderText = (field) => {
    if (field?.search_format_helper && field?.search_format_helper !== '') {
      return field.label_name + " (" + field?.search_format_helper + ")"
    } else {
      return field.label_name
    }
  }

  useEffect(() => {
    if (module?.code_key) {
      // hiding the advance search when module changes
      setShowAdvancedSearch(false);
      setAdvancedSearchFilters([]);
    }
  }, [module?.code_key])

  useEffect(() => {
    if (active.data.limit_to_portfolio_id || active?.spoofed_user?.limit_to_portfolio_id) {
      let limitedPortfolioIds = active?.spoofed_user?.limit_to_portfolio_id ?? (active.data.limit_to_portfolio_id);
      let limitedPortfolio = []
      limitedPortfolioIds.forEach((portfolioId) => {
        let portfolioData = portfolios.data.filter((portfolio) => portfolio.id === portfolioId);
        portfolioData = portfolioData[0] ?? null
        if (portfolioData) {
          limitedPortfolio.push(portfolioData)
        }
      })
      setValidPortfolios(limitedPortfolio)
    } else if (active.filtered_portfolio) {
      setValidPortfolios(portfolios.data.filter((p) => p.id === active.filtered_portfolio));
    } else {
      setValidPortfolios(portfolios.data);
    }
  }, [active, portfolios])

  const textToDisplay = filterLocally ? 'Filter By ' : 'Search By '
  const filterPlaceholder = (textToDisplay + searchableFields.map((field) => buildPlaceholderText(field)).join(', '))

  const excludeFromLastFour = ["bank_account"];

  const advancedSearchFields = {
    customer: [
      { name: 'first_name', column_name: 'first_name', type: 'text' },
      { name: 'last_name', column_name: 'last_name', type: 'text' },
      { name: 'email_address', column_name: 'email_address', type: 'text' },
      { name: 'portfolio_id', column_name: 'portfolio_id', type: 'ddo' },
      { name: 'address', column_name: 'address', type: 'text' },
      { name: 'city', column_name: 'city', type: 'text' },
      { name: 'state_dropdown_option_id', column_name: 'state_dropdown_option_id', type: 'ddo', ddo_key: 'state' },
      { name: 'zip_code_id', column_name: 'zip_code_id', type: 'ddo_searchable', ddo_key: 'zip_code' },
      { name: 'birth_date', column_name: 'birth_date', type: 'date' },
      { name: 'ssn_last_four', column_name: 'ssn_last_four', type: 'text' },
      { name: 'Full SSN', column_name: 'ssn_encrypted', type: 'text' },
      { name: 'is_ssp_disabled', column_name: 'is_ssp_disabled', type: 'boolean' },
      { name: 'is_debit_card_disabled', column_name: 'is_debit_card_disabled', type: 'boolean' },
      { name: 'is_consent_email_transactional', column_name: 'is_consent_email_transactional', type: 'boolean' },
      { name: 'is_consent_email_marketing', column_name: 'is_consent_email_marketing', type: 'boolean' },
      { name: 'is_consent_sms_transactional', column_name: 'is_consent_sms_transactional', type: 'boolean' },
      { name: 'is_consent_sms_marketing', column_name: 'is_consent_sms_marketing', type: 'boolean' },
      { name: 'is_consent_phone_marketing', column_name: 'is_consent_phone_marketing', type: 'boolean' },
      { name: 'is_do_not_call', column_name: 'is_do_not_call', type: 'boolean' },
      { name: 'is_home_phone_do_not_call', column_name: 'is_home_phone_do_not_call', type: 'boolean' },
      { name: 'is_cell_phone_do_not_call', column_name: 'is_cell_phone_do_not_call', type: 'boolean' },
      { name: 'is_work_phone_do_not_call', column_name: 'is_work_phone_do_not_call', type: 'boolean' },
      { name: 'is_returning_customer', column_name: 'is_returning_customer', type: 'boolean' },
      { name: 'customer_status_dropdown_option_id', column_name: 'customer_status_dropdown_option_id', type: 'ddo', ddo_key: 'customer_status' },
      { name: 'nonfundable_reason_dropdown_option_id', column_name: 'nonfundable_reason_dropdown_option_id', type: 'ddo', ddo_key: 'nonfundable_reason' },
      { name: 'language_dropdown_option_id', column_name: 'language_dropdown_option_id', type: 'ddo', ddo_key: 'language' },
    ],
    lead: [
      { name: 'is_express_loan', column_name: 'is_express_loan', type: 'boolean' },
      { name: 'is_returning_customer', column_name: 'is_returning_customer', type: 'boolean' },
      { name: 'is_redirected_to_our_site', column_name: 'is_redirected_to_our_site', type: 'boolean' },
      { name: 'is_ssv_extra_ribbitbanklogin', column_name: 'is_ssv_extra_ribbitbanklogin', type: 'boolean' },
      { name: 'lead_status_dropdown_option_id', column_name: 'lead_status_dropdown_option_id', type: 'ddo', ddo_key: 'lead_status' },
      { name: 'loan_funding_method_dropdown_option_id', column_name: 'loan_funding_method_dropdown_option_id', type: 'ddo', ddo_key: 'loan_funding_method' },
      { name: 'payment_method_dropdown_option_id', column_name: 'payment_method_dropdown_option_id', type: 'ddo', ddo_key: 'payment_method' },
      { name: 'lead_approval_status_dropdown_option_id', column_name: 'lead_approval_status_dropdown_option_id', type: 'ddo', ddo_key: 'lead_approval_status' },
      { name: 'lead_withdrawal_category_dropdown_option_id', column_name: 'lead_withdrawal_category_dropdown_option_id', type: 'ddo', ddo_key: 'lead_withdrawal_category' },
      { name: 'lead_withdrawal_reason_dropdown_option_id', column_name: 'lead_withdrawal_reason_dropdown_option_id', type: 'ddo', ddo_key: 'lead_withdrawal_reason' },
      { name: 'lead_cancel_reason_dropdown_option_id', column_name: 'lead_cancel_reason_dropdown_option_id', type: 'ddo', ddo_key: 'lead_cancel_reason' },
      { name: 'verification_action_source_dropdown_option_id', column_name: 'verification_action_source_dropdown_option_id', type: 'ddo', ddo_key: 'action_source' },
      { name: 'state_dropdown_option_id', column_name: 'state_dropdown_option_id', type: 'ddo', ddo_key: 'state' },
      { name: 'is_refinanced', column_name: 'refinanced_loan_id', type: 'boolean', customize_label: 'Is Refinanced' },
    ],
    loan: [
      { name: 'pay_frequency_dropdown_option_id', column_name: 'pay_frequency_dropdown_option_id', type: 'ddo', ddo_key: 'pay_frequency' },
      { name: 'loan_status_dropdown_option_id', column_name: 'loan_status_dropdown_option_id', type: 'ddo', ddo_key: 'loan_status' },
      { name: 'loan_sub_status_dropdown_option_id', column_name: 'loan_sub_status_dropdown_option_id', type: 'ddo', ddo_key: 'loan_sub_status' },
      { name: 'loan_funding_method_dropdown_option_id', column_name: 'loan_funding_method_dropdown_option_id', type: 'ddo', ddo_key: 'loan_funding_method' },
      { name: 'ach_revocation_reason_dropdown_option_id', column_name: 'ach_revocation_reason_dropdown_option_id', type: 'ddo', ddo_key: 'ach_revocation_reason' },
      { name: 'state_dropdown_option_id', column_name: 'state_dropdown_option_id', type: 'ddo', ddo_key: 'state' },
      { name: 'refinance_reason_dropdown_option_id', column_name: 'refinance_reason_dropdown_option_id', type: 'ddo', ddo_key: 'refinance_reason' },
    ],
    payment: [
      { name: 'payment_return_code_dropdown_option_id', column_name: 'payment_return_code_dropdown_option_id', type: 'ddo', ddo_key: 'payment_return_code' },
      { name: 'payment_correction_code_dropdown_option_id', column_name: 'payment_correction_code_dropdown_option_id', type: 'ddo', ddo_key: 'payment_correction_code' },
      { name: 'payment_status_dropdown_option_id', column_name: 'payment_status_dropdown_option_id', type: 'ddo', ddo_key: 'payment_status' },
      { name: 'payment_method_dropdown_option_id', column_name: 'payment_method_dropdown_option_id', type: 'ddo', ddo_key: 'payment_method' },
      { name: 'payment_type_dropdown_option_id', column_name: 'payment_type_dropdown_option_id', type: 'ddo', ddo_key: 'payment_type' },
      { name: 'action_source_dropdown_option_id', column_name: 'action_source_dropdown_option_id', type: 'ddo', ddo_key: 'action_source' },
      { name: 'authorization_type_dropdown_option_id', column_name: 'authorization_type_dropdown_option_id', type: 'ddo', ddo_key: 'authorization_type' },
      { name: 'mgr_approval_status_dropdown_option_id', column_name: 'mgr_approval_status_dropdown_option_id', type: 'ddo', ddo_key: 'mgr_approval_status' },
      { name: "scheduled_date", column_name: "scheduled_date", type: "date_range" },
    ],
    template: [
      { name: 'is_active', column_name: 'is_active', type: 'boolean' },
      { name: 'is_transactional', column_name: 'is_transactional', type: 'boolean' },
      { name: 'email_action_dropdown_option_id', column_name: 'email_action_dropdown_option_id', type: 'ddo', ddo_key: 'email_action' },
      { name: 'template_group_dropdown_option_id', column_name: 'template_group_dropdown_option_id', type: 'ddo', ddo_key: 'template_group' },
      { name: 'portfolio_id', column_name: 'portfolio_id', type: 'ddo' },
    ],
    rc_calling_recording: [
      { name: 'loan_id', column_name: 'loan_id', type: 'text' },
      { name: 'Customer Id', column_name: 'customer_id', type: 'text' },
      { name: 'Portfolio', column_name: 'portfolio_id', type: 'ddo' },
      { name: 'Loan Status', column_name: 'loan_status_dropdown_option_id', type: 'ddo', ddo_key: 'loan_status' },
      { name: "end_datetime", column_name: "end_datetime", type: "date_range" },
    ],
    non_fundable_state_change: [
      { name: 'Portfolio', column_name: 'portfolio_id', type: 'ddo' },
      { name: 'New State', column_name: 'new_state_dropdown_option_id', type: 'ddo', ddo_key: 'state' },
      { name: 'is_consent_email_transactional', column_name: 'is_consent_email_transactional', type: 'boolean' },
      { name: 'is_consent_email_marketing', column_name: 'is_consent_email_marketing', type: 'boolean' },
      { name: 'is_do_not_call', column_name: 'is_do_not_call', type: 'boolean' },
    ],
    manager_approval: [
      { name: 'Approval Status', column_name: 'mgr_approval_status_dropdown_option_id', type: 'ddo', ddo_key: 'mgr_approval_status' },
      { name: 'Loan Status', column_name: 'loans.loan_status_dropdown_option_id', type: 'ddo', ddo_key: 'loan_status' },
      { name: 'Lead Status', column_name: 'leads.lead_status_dropdown_option_id', type: 'ddo', ddo_key: 'lead_status' },
      { name: 'Customer First Name', column_name: 'customers.first_name', type: 'text' },
      { name: 'Customer Last Name', column_name: 'customers.last_name', type: 'text' },
      { name: 'Agent First Name', column_name: 'users.first_name', type: 'text' },
      { name: 'Agent Last Name', column_name: 'users.last_name', type: 'text' },
    ],
    campaign: [
      { name: "Created At", column_name: "created_at", type: "date_range" },
      { name: "Campaign Type", column_name: "campaign_type_dropdown_option_id", type: "ddo", ddo_key: "campaign_type" }
    ],
    sensitive_pii_view_log: [
      { name: "Created At", column_name: "created_at", type: "date_range" },
      { name: "Sensitive Data Type", column_name: "sensitive_pii_type_dropdown_option_id", type: "ddo", ddo_key: "sensitive_pii_type" },
      { name: "Sensitive Pii View Reason", column_name: "sensitive_pii_view_reason_dropdown_option_id", type: "ddo", ddo_key: "sensitive_pii_view_reason" }
    ],
    loan_dmc: [
      { name: 'Loan ID', column_name: 'loan_id', type: 'text' },
      { name: 'DMC Name', column_name: 'dmcs.name', type: 'text' },
      { name: 'Customer First Name', column_name: 'customers.first_name', type: 'text' },
      { name: 'Customer Last Name', column_name: 'customers.last_name', type: 'text' },
      { name: 'is_active', column_name: 'is_active', type: 'boolean' },
      { name: 'DMC Status', column_name: 'dmc_status_dropdown_option_id', type: 'ddo', ddo_key: 'dmc_status' },
    ],
    dmc: [
      { name: 'name', column_name: 'name', type: 'text' },
      { name: 'email_address', column_name: 'email_address', type: 'text' },
      { name: 'is_active', column_name: 'is_active', type: 'boolean' },
      { name: 'has_business_license', column_name: 'has_business_license', type: 'boolean' },
    ],
    customer_debit_card: [
      { name: 'Customer', column_name: 'customer_id', type: 'text', placeholder: 'Name or ID' },
      { name: 'Card Holder Name', column_name: 'card_holder_name', type: 'text' },
      { name: 'Card Type', column_name: 'card_type_dropdown_option_id', type: 'ddo', ddo_key: 'card_type' },
      { name: 'Card Last 4 Digits', column_name: 'last_four', type: 'text', max_length: 4, placeholder: '####', digits_only: true },
      // The API matches the date and amount ranges against the same processor charge, not any two charges.
      { name: 'Transaction Date Range', column_name: 'transaction_date', type: 'range', input_type: 'date' },
      { name: 'Transaction Amount Range', column_name: 'transaction_amount', type: 'range', input_type: 'number', placeholders: ['$ Min', '$ Max'] },
    ]
  }

  const defaultValues = (advancedSearchFields[module?.code_key] || []).reduce((acc, field) => {
    acc[field.name] = field.type === 'boolean' ? null : '';
    if (field.type === 'date_range') {
      acc[`${field.name}_1`] = "";
      acc[`${field.name}_2`] = "";
    }
    if (field.type === 'range') {
      acc[`${field.name}_from`] = "";
      acc[`${field.name}_to`] = "";
    }
    return acc;
  }, {});

  const { control, reset } = useForm({ defaultValues });

  const handleReset = (fullReset = true) => {
    if (fullReset) {
      setShowAdvancedSearch(false);
    }
    reset(defaultValues);
    setAdvancedSearchFilters([]);
  };

  const createAdvancedFilterJson = (column_name, value, type, optionalArrayIndex) => {
    let existingValues = [...advancedSearchFilters]; // create a copy of the array to avoid direct mutation
    // debugger; // eslint-disable-line no-debugger
    let operator = "=";
    const index = existingValues.findIndex((item) => item.column === column_name);

    if (type === 'text') {
      if (value?.includes('%')) {
        operator = 'like';
        // handling values with %123% for like operator
        value = encodeURIComponent(value);
      }
    }
    if (type === 'date_range') {
      operator = 'between'
    }
    if (value == null) {
      if (type === 'date_range') {
        if (index !== -1 && !existingValues[index].value[optionalArrayIndex === 0 ? 1 : 0]) {
          existingValues = existingValues.filter((item) => item.column !== column_name);
        } else {
          existingValues[index].value[optionalArrayIndex] = null;
        }
      } else {
        // remove from the array
        existingValues = existingValues.filter((item) => item.column !== column_name);
      }
    } else {
      if (column_name === 'refinanced_loan_id') {
        operator = value === "1" ? '!=' : '=';
        value = value === "1" ? "null" : null; // backend is handling in this way
      }

      if (index !== -1) {
        // replace the value
        if (type === 'date_range') {
          existingValues[index].value[optionalArrayIndex] = value
        } else {
          existingValues[index] = { column: column_name, value: value, operator: operator };
        }
      } else if (type === 'date_range') {
        const arr = [];
        arr[optionalArrayIndex] = value;
        existingValues.push({ column: column_name, value: arr, operator: operator });
      } else {
        // set the value

        existingValues.push({ column: column_name, value: value, operator: operator });
      }
    }
    setAdvancedSearchFilters(existingValues);
  }

  //This will delay 0.5 second to hit search after user stop enter text in search box
  useEffect(() => {
    if (queryText !== null) {
      createAdvancedFilterJson(queryText?.column_name, queryText?.value, queryText?.type)
      setQueryText(null);
    }
  }, [queryText]);

  const getDropdownOptions = (item) => {
    if (item.column_name === 'portfolio_id') {
      return validPortfolios?.filter((o) => o.id != null).map((o) => ({ label: o.name, value: o.id })) || []
    } else {
      return dropDowns?.data?.find((x) => x.code_key == item.ddo_key)?.dropdown_options?.filter((o) => o.code_key != 'pay_frequency_daily').map((x) => ({ label: x.name, value: x.id })) || []
    }
  }

  useEffect(() => {
    if (!showAdvancedSearch) {
      handleSearch(null);
    }
  }, [advancedSearchFilters, showAdvancedSearch]);

  const renderHeader = (field) => {
    let column_name = field.name
    const data = module.module_fields.find(
      (header) => header.column_name === column_name
    )
    if (data?.label_name) {
      return data?.label_name
    } else if (field?.customize_label) {
      return field?.customize_label;
    } else {
      return column_name
        .split('_')
        .join(' ');
    }
  }

  useEffect(() => {
    //Check queryText to make sure if user enter text to search and delay time to move focus to main search
    if (!showAdvancedSearch && queryText === null) {
      searchRef.current.focus();
    }
  });

  /**
   * this function is used to filter the data locally
   * @param {*} data
   * @param {*} filters
   * @returns
   * This function will return an array of data that matches the filters
   */
  function applyFilters(data, filters) {
    return data.filter((item) => {
      return filters.some((filter) => {
        const { column, value, operator } = filter
        if (operator === "like") {
          try {
            const regex = new RegExp(value.replace(/%/g, ''), 'i')
            return regex.test(item[column])
          } catch (e) {
            return false;
          }
        } else if (operator === "=") {
          //if the operator is '=' , then no need to include %, it will cause can't not compare
          const valueSearch = value.replace('%', '')
          return item[column] == valueSearch
        }
        return true // Return true for filters without a valid operator
      })
    })
  }

  /**
   *
   * @param {*} searchKeyWords
   * @returns
   * This function will return an array of key value pairs that can be used to filter the data
   * Example:
   * array of {
   *       column: searchableField,
   *       value: `${searchKeyWords}`,
   *       operator: 'like' || 'equal',
   *     }
   *  This will also handle dropdowns and encrypted fields accordingly their data type
   */

  const searchableKeyValuesPair = (searchKeyWords) => {
    const keyValuePair = [];
    searchableFields.map((field) => {
      let already_added = false;
      const value = (!Number.isNaN(searchKeyWords)) ? `${searchKeyWords}%` : `%${searchKeyWords}%`
      const fieldType = dropdownOptions[headers.find((x) => x.column_name == field.column_name)?.module_field_type_dropdown_option_id]?.code_key
      let column_name = field.column_name

      switch (fieldType) {
        case MODULE_FIELD_TYPES.ENCRYPTED.code_key:
          if (excludeFromLastFour.includes(module.code_key)) {
            keyValuePair.push({
              column: `${column_name}_encrypted`,
              value: `${value}`,
              operator: '=',
            })
            already_added = true;
          }
          column_name += "_last_four"
          break;

        case MODULE_FIELD_TYPES.DROPDOWN_SEARCHABLE.code_key:
        case MODULE_FIELD_TYPES.DROPDOWN.code_key:
          keyValuePair.push({
            column: column_name,
            value: `${value}`,
            operator: '=',
          })
          already_added = true;
          // TODO: Find a better way to search on a dropdown/dropdown_searchable.
          // This does not work against prod data sets.
          // It's trying to look up against <model>_label e.g. customer_label across 3 million rows.
          // Even though the helper says e.g, 123 we are not searching against an id
          // Removing this code and adding the code above makes it so we just search on the id.
          // column_name = column_name.replace(column_name.substr(-3), '_label')
          break;

        case MODULE_FIELD_TYPES.DROPDOWN_SYSTEM.code_key:
          const code_key = column_name.replace("_dropdown_option_id", "")
          dropDowns?.data?.find((x) => x.code_key == code_key)?.dropdown_options?.map((x) => {
            const regex = new RegExp(value.replace(/%/g, ''), 'i')
            if (regex.test(x.name)) {
              keyValuePair.push({
                column: column_name,
                value: `${x.id}`,
                operator: '=',
              })
            }
          })
          already_added = true;
          break;
      }
      if (!already_added) {
        keyValuePair.push({
          column: column_name,
          value: `${value}`,
          operator: 'like',
        })
      }
    })
    return keyValuePair;
  }

  const handleSearch = async (x) => {
    x?.preventDefault()
    const searchKeyWords = x?.target?.value
    let searchableKeyValueObjArray = null
    setSearchKeyWords(searchKeyWords)

    if (!searchKeyWords) {
      clearSearchAndReload()
    } else {
      searchableKeyValueObjArray = searchableKeyValuesPair(searchKeyWords)
    }

    if (!originalDataBeforeFiltering) {
      setOriginalDataBeforeFiltering(moduleData)
    }

    const dataSet = originalDataBeforeFiltering?.data ? originalDataBeforeFiltering?.data : moduleData.data;
    if (!showAdvancedSearch && filterLocally && searchableKeyValueObjArray) {
      const appliedFiltered = applyFilters(dataSet, searchableKeyValueObjArray)
      if (Array.isArray(appliedFiltered)) {
        const filteredData = { ...moduleData, data: appliedFiltered }
        filteredData.meta = { ...filteredData.meta, total: appliedFiltered.length }
        await dispatch(
          fetchModuleData({
            preloadedData: filteredData,
          })
        )
        return;
      }
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout)
      setSearchTimeout(0)
    }

    if (showAdvancedSearch || (searchableKeyValueObjArray && !filterLocally)) {
      setSearchTimeout(setTimeout(async () => {
        setIsSearching(true)
        setPartialLoad(false)
        setFilters(searchableKeyValueObjArray)
        setSearchFilter(filters)
        let urlWithFilters;
        if (active.filtered_portfolio) {
          urlWithFilters = concatFiltersOnUrl(url, searchableKeyValueObjArray, advancedSearchFilters, { column: 'portfolio_id', value: active.filtered_portfolio, operator: '=' })
        } else {
          urlWithFilters = concatFiltersOnUrl(url, searchableKeyValueObjArray, advancedSearchFilters)
        }
        try {
          await dispatch(
            fetchModuleData({
              accessToken: auth.accessToken,
              module_endpoint: urlWithFilters,
            })
          )
          setPartialLoad(true)
          setIsSearching(false)
        } catch (error) {
          setPartialLoad(true)
          setIsSearching(false)
        }
      }, showAdvancedSearch ? 0 : 1000))
    }
  }

  const clearSearchAndReload = () => {
    if (!showAdvancedSearch) {
      setSearchKeyWords('')
      setFilters([])
    }
    let urlClone = url;
    if (active.filtered_portfolio) {
      urlClone = concatFiltersOnUrl(url, [{ column: 'portfolio_id', value: active.filtered_portfolio, operator: '=' }])
    }
    setPartialLoad(true)
    if (!showAdvancedSearch && originalDataBeforeFiltering) {
      dispatch(
        fetchModuleData({
          preloadedData: originalDataBeforeFiltering,
        })
      )
      setOriginalDataBeforeFiltering(null)
    } else if (!showAdvancedSearch) {
      dispatch(
        fetchModuleData({
          accessToken: auth.accessToken,
          module_endpoint: urlClone,
        })
      )
    }
  }

  return (
    <div className="lox-table-filtering notification p-2 m-0">
      {!showAdvancedSearch && (
        <div className="level">
          <div className={`field is-12 ${advancedSearchFields[module?.code_key] ? 'has-addons' : ''}`} style={{ width: '100%' }}>
            <div className="control has-icons-left has-icons-right" style={{ width: '100%' }}>
              <input
                className="input"
                type="text"
                value={searchKeyWords || ''}
                placeholder={filterPlaceholder}
                onChange={handleSearch}
                ref={searchRef}
              />
              <span className="icon is-small is-left">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              {searchKeyWords && (
                <span
                  className="icon is-small is-right is-clickable"
                  onClick={() => clearSearchAndReload()}
                >
                  <FontAwesomeIcon icon={faClose} />
                </span>
              )}
            </div>
            <div className="control">
              {advancedSearchFields[module?.code_key] && (
                <a
                  className="button has-text-link px-4"
                  onClick={() => {
                    setShowAdvancedSearch(true);
                    setAdvancedSearchFilters([]);
                  }}
                >
                  Advanced Search
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {(showAdvancedSearch && advancedSearchFields[module?.code_key]) ? (
        <form id="search_form">
          {(advancedSearchFields[module?.code_key] && module?.code_key == 'customer') ? (
            <SpecialInstruction
              content={'<span>To use fuzzy search use a &apos;%&apos; symbol to find a record. Ex: to find all customers with the first name with &apos;nathan&apos; in their name type %nathan%, this will return Jonathan and Nathan, if you are looking for an email that ends in gmail.com search for %gmail.com  .</span>'}
              type="is-warning"
              padding="p-0 pb-2"
            />
          ) : null}
          {(advancedSearchFields[module?.code_key] && (module?.code_key == 'dmc' || module?.code_key == 'loan_dmc')) ? (
            <SpecialInstruction
              content={'<span>To use fuzzy search use a &apos;%&apos; symbol to find a record. Ex: to find all dmc&apos;s with a customer with the first name with &apos;nathan&apos; in their name type %nathan%, this will return Jonathan and Nathan, if you are looking for an email that ends in gmail.com search for %gmail.com  .</span>'}
              type="is-warning"
              padding="p-0 pb-2"
            />
          ) : null}
          <div className={`columns is-multiline pb-0`} style={{ zIndex: 15, position: 'relative' }}>
            {advancedSearchFields[module?.code_key] &&
              advancedSearchFields[module?.code_key].map((item) => (
                <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0" key={item.name}>
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">{renderHeader(item)}</span>
                  </p>
                  <div>
                    {item.type == 'boolean' && (
                      <Controller
                        name={item.name}
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={[{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]}
                            isClearable
                            isSearchable={false}
                            classNames={reactSelectClassNames()}
                            onChange={(evt) => {
                              field.onChange(evt ? { label: evt?.label, value: (evt?.value) } : null);
                              createAdvancedFilterJson(item.column_name, evt?.value || null, item.type);
                            }}
                            styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                            menuPosition="fixed"
                            form="search_form"
                          />
                        )}
                      />
                    )}

                    {item.type == 'ddo' && (
                      <Controller
                        name={item.name}
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={getDropdownOptions(item)}
                            isClearable
                            isSearchable={false}
                            classNames={reactSelectClassNames()}
                            onChange={(evt) => {
                              field.onChange(evt ? { label: evt?.label, value: (evt?.value) } : null);
                              createAdvancedFilterJson(item.column_name, evt?.value || null, item.type);
                            }}
                            styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                            menuPosition="fixed"
                            form="search_form"
                          />
                        )}
                      />
                    )}

                    {item.type === 'ddo_searchable' && (() => {
                      let typingTimeout = null;
                      const loadOptions = async (inputValue) => {
                        return new Promise((resolve) => {
                          if (typingTimeout) clearTimeout(typingTimeout);
                          typingTimeout = setTimeout(() => {
                            const filterQuery = {
                              and: [
                                { column: item.ddo_key, value: inputValue, operator: 'like' },
                              ],
                            };

                            request.setEndpoint(
                              `${process.env.REACT_APP_API_DOMAIN}/${item.ddo_key}?filters=${JSON.stringify(filterQuery)}`
                            );
                            const response = request.get().then((response) => response.data.data);
                            resolve(response);
                          }, 1000);
                        });
                      };

                      return (
                        <div>
                          <Controller
                            name={item.column_name}
                            control={control}
                            render={({ field }) => (
                              <AsyncSelect
                                {...field}
                                isClearable
                                isSearchable
                                defaultOptions={false}
                                loadOptions={loadOptions}
                                getOptionLabel={(e) => parseLabel(e)}
                                getOptionValue={(e) => parseLabel(e)}
                                classNames={reactSelectClassNames()}
                                onChange={(evt) => {
                                  field.onChange(evt ? { label: evt?.label, value: (evt?.value) } : null);
                                  createAdvancedFilterJson(item.column_name, evt?.id || null, item.type);
                                }}
                                menuPlacement="auto"
                              />
                            )}
                          />
                        </div>
                      );
                    })()}

                    {item.type == 'text' && (
                      <Controller
                        name={item.name}
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <input
                            {...field}
                            className="input is-small"
                            type="text"
                            form="search_form"
                            maxLength={item.max_length}
                            placeholder={item.placeholder}
                            inputMode={item.digits_only ? 'numeric' : undefined}
                            onChange={(evt) => {
                              const value = item.digits_only ? evt?.target?.value?.replace(/\D/g, '') : evt?.target?.value
                              field.onChange(value)
                              setQueryText({ column_name: item.column_name, value: value || null, type: item.type })
                            }}
                          />
                        )}
                      />
                    )}

                    {item.type == 'date' && (
                      <Controller
                        name={item.name}
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <input
                            {...field}
                            className="input is-small"
                            type="date"
                            form="search_form"
                            onChange={(evt) => {
                              field.onChange(evt?.target?.value)
                              createAdvancedFilterJson(item.column_name, evt?.target?.value || null, item.type);
                            }}
                          />
                        )}
                      />
                    )}

                    {item.type === 'range' && (
                      <div className="is-flex">
                        {['from', 'to'].map((bound, index) => (
                          <div className={index === 0 ? 'mr-2' : ''} key={bound}>
                            <Controller
                              name={`${item.name}_${bound}`}
                              control={control}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  className="input is-small"
                                  type={item.input_type}
                                  min={item.input_type === 'number' ? 0 : undefined}
                                  step={item.input_type === 'number' ? '0.01' : undefined}
                                  placeholder={item.placeholders?.[index]}
                                  aria-label={`${item.name} ${bound === 'from' ? 'start' : 'end'}`}
                                  form="search_form"
                                  onChange={(evt) => {
                                    field.onChange(evt?.target?.value)
                                    createAdvancedFilterJson(`${item.column_name}_${bound}`, evt?.target?.value || null, item.type);
                                  }}
                                />
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {item.type === 'date_range' && (
                      <div className="is-flex">
                        <p className="has-text-grey is-size-6">
                          <span className="mr-1">Start</span>
                        </p>
                        <div className="mr-2">
                          <Controller
                            name={`${item.name}_1`}
                            control={control}
                            render={({ field }) => (
                              <input
                                {...field}
                                className="input is-small"
                                type="date"
                                form="search_form"
                                onChange={(evt) => {
                                  field.onChange(evt?.target?.value)
                                  createAdvancedFilterJson(item.column_name, evt?.target?.value + " 00:00:00", 'date_range', 0);
                                }}
                              />
                              // <DatePicker
                              //   showIcon
                              //   selected={field.value || null}
                              //   classNames={reactSelectClassNames()}
                              //   onChange={(date) => {
                              //     field.onChange(date)
                              //     createAdvancedFilterJson(item.column_name, date, 'date_range', 0);
                              //   }}
                              //   icon={<FontAwesomeIcon icon={faCalendar} className={'is-size-6 pr-1'} />}
                              //   placeholderText={'mm/dd/yyyy'}
                              // />
                            )}
                          />
                        </div>
                        <p className="has-text-grey is-small is-size-8">
                          <span className="mr-1">End</span>
                        </p>
                        <div>
                          <Controller
                            name={`${item.name}_2`}
                            control={control}
                            render={({ field }) => (
                              <input
                                {...field}
                                className="input is-small"
                                type="date"
                                form="search_form"
                                onChange={(evt) => {
                                  field.onChange(evt?.target?.value)
                                  createAdvancedFilterJson(item.column_name, evt?.target?.value + " 23:59:59", 'date_range', 1);
                                }}
                              />
                              // <DatePicker
                              //   showIcon
                              //   selected={field.value || null}
                              //   classNames={reactSelectClassNames()}
                              //   onChange={(date) => {
                              //     field.onChange(date)
                              //     createAdvancedFilterJson(item.column_name, date, 'date_range', 1);
                              //   }}
                              //   icon={<FontAwesomeIcon icon={faCalendar} className={'is-size-6 pr-1'} />}
                              //   placeholderText={'mm/dd/yyyy'}
                              // />
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
          <div className="lox-form-button-wrapper has-text-right">
            <button
              type="button"
              className="button has-background-grey has-text-white is-small"
              onClick={() => handleReset(true)}
              disabled={isSearching}
            >
              <span className="mx-3">Cancel</span>
            </button>

            <button
              type="button"
              className="button has-background-grey has-text-white is-small"
              onClick={() => handleReset(false)}
              disabled={isSearching}
            >
              <span className="mx-3">Clear</span>
            </button>

            <button
              type="button"
              className="button is-link has-text-white is-small"
              onClick={handleSearch}
              disabled={isSearching}
            >
              <span className="mx-3">Search</span>
            </button>
          </div>

        </form>
      ) : null}
    </div>
  )
}
